import { SpanStatusCode, trace, type Span } from '@opentelemetry/api'
import { SystemError, UserError } from 'shared/utils/errors'

import logger from '../logger'
import {
  Metadata,
  ServerActionErrorResponse,
  ServerActionResponse,
} from './server-action.schema'

/**
 * This method processes a server-action response, throwing an error if the error property is not null
 * it is used in both the client and server, so keep it generic and not server-specific
 * @throws {UserError} if the response.error property is not null
 */
export async function processServerAction<T>(
  request: Promise<ServerActionResponse<T>>,
  defaultErrorMessage: string,
  options?: {
    operationName?: string
    /**
     * Relative importance of this action.
     *
     * Descriptive only — it is recorded as a span attribute but no longer
     * drives a sampling rate the way a trace sampler would have.
     * Most backends sample at ingest.
     */
    sampleRate?: 'VERY_HIGH' | 'HIGH' | 'MEDIUM' | 'LOW' | 'VERY_LOW' | 'NONE'
  },
): Promise<T> {
  // Only open a span if operationName is provided
  if (options?.operationName) {
    const tracer = trace.getTracer('server-action')

    return await tracer.startActiveSpan(
      options.operationName,
      {
        attributes: {
          'server_action.name': options.operationName,
          'server_action.sample_rate': options.sampleRate ?? 'LOW',
        },
      },
      async (span) => {
        try {
          return await processRequest(
            request,
            defaultErrorMessage,
            span,
            options.operationName,
          )
        } finally {
          span.end()
        }
      },
    )
  }

  // No tracing - original behavior
  return await processRequest(request, defaultErrorMessage)
}

async function processRequest<T>(
  request: Promise<ServerActionResponse<T>>,
  defaultErrorMessage: string,
  span?: Span,
  operationName?: string,
): Promise<T> {
  let errorMessage: string | null = null
  let metadata: Metadata | null = null
  let originalError: unknown = null
  let caughtException = false

  try {
    const response = await request

    if (isErrorResponse(response)) {
      errorMessage = response.error.message || defaultErrorMessage
      metadata = response.error.metadata
      originalError = response.error

      span?.setStatus({ code: SpanStatusCode.ERROR, message: errorMessage })
    } else {
      span?.setStatus({ code: SpanStatusCode.OK })
      return response.response
    }
  } catch (error) {
    caughtException = true
    originalError = error
    errorMessage = defaultErrorMessage

    span?.setStatus({ code: SpanStatusCode.ERROR, message: errorMessage })
  }

  if (operationName) {
    // This is not a user error, it's an unknown error that should be logged so we can see why it's failing
    const userActionError = new SystemError(defaultErrorMessage, {
      feature: logger.Feature.ServerAction,
      operationName: operationName,
      originalError,
    })

    logger.error(userActionError, {
      feature: logger.Feature.ServerAction,
      key: operationName,
    })
  } else if (caughtException) {
    // Original behavior: only log caught exceptions for non-key operations
    logger.error(originalError, { feature: logger.Feature.ServerAction })
  }

  const handledError = new UserError(errorMessage, metadata || undefined)

  throw handledError
}

function isErrorResponse<T>(
  response: ServerActionResponse<T>,
): response is ServerActionErrorResponse {
  return response.error !== null
}
